import React, { useMemo, useState } from "react";
import "./ShippingPolicy.css";

function AccordionItem({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`spAccItem ${open ? "open" : ""}`}>
      <button
        type="button"
        className="spAccHeader"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="spAccIcon" aria-hidden="true">{icon}</span>
        <span className="spAccTitle">{title}</span>
        <span className="spAccChevron" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>

      <div className="spAccBody" style={{ display: open ? "block" : "none" }}>
        {children}
      </div>
    </div>
  );
}

export default function ShippingPolicy() {
  const highlights = useMemo(
    () => [
      { label: "From ₹49 India Shipping", value: "Domestic" },
      { label: "3–10 Working Days", value: "ETA" },
      { label: "Delhivery Partner", value: "Courier" },
      { label: "Unboxing Video Mandatory", value: "Claims" },
    ],
    []
  );

  return (
    <div className="spPage">
      <div className="spWrap">
        {/* HERO HEADER */}
        <div className="spHero">
          <div className="spHeroTop">
            <h1 className="spTitle">Shipping & Delivery Policy</h1>
            <p className="spSub">
              At Ayrine, we carefully pack and dispatch every order to ensure it reaches you safely.
              Please review our shipping terms before placing your order.
            </p>
          </div>

          <div className="spHighlights">
            {highlights.map((h) => (
              <div className="spChip" key={h.label}>
                <span className="spChipTop">{h.value}</span>
                <span className="spChipText">{h.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="spGrid">
          {/* LEFT: FLOW / TIMELINE */}
          <div className="spCard spFlow">
            <div className="spCardHead">
              <h2 className="spCardTitle">How delivery works</h2>
              <p className="spCardSub">A quick, clear flow so customers know what to expect.</p>
            </div>

            <div className="spTimeline">
              <div className="spStep">
                <div className="spDot" />
                <div className="spStepBody">
                  <div className="spStepTitle">Order placed</div>
                  <div className="spStepText">Please double-check address, PIN code, and phone number.</div>
                </div>
              </div>

              <div className="spStep">
                <div className="spDot" />
                <div className="spStepBody">
                  <div className="spStepTitle">Packed & verified</div>
                  <div className="spStepText">Every order is carefully packed before dispatch.</div>
                </div>
              </div>

              <div className="spStep">
                <div className="spDot" />
                <div className="spStepBody">
                  <div className="spStepTitle">Shipped via Delhivery</div>
                  <div className="spStepText">
                    You’ll receive a tracking ID + link. Please track regularly.
                  </div>
                </div>
              </div>

              <div className="spStep">
                <div className="spDot" />
                <div className="spStepBody">
                  <div className="spStepTitle">Delivered</div>
                  <div className="spStepText">
                    Stay reachable for courier calls. If returned due to customer-related issues,
                    re-dispatch requires paying shipping again.
                  </div>
                </div>
              </div>
            </div>

            <div className="spNoticeRow">
              <div className="spNotice warn">
                <div className="spNoticeTitle">Important</div>
                <div className="spNoticeText">
                  Re-dispatch is processed only after paying shipping charges again. Refunds won’t be provided in such cases.
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: ACCORDION SECTIONS */}
          <div className="spCard spDetails">
            <div className="spCardHead">
              <h2 className="spCardTitle">Policy details</h2>
              <p className="spCardSub">Expandable sections for a clean, modern look.</p>
            </div>

            <div className="spAccordion">
              <AccordionItem title="Shipping partner" icon="🚚" defaultOpen>
                <p>
                  All domestic orders are shipped through our trusted logistics partner, <b>Delhivery</b>.
                </p>
                <p>
                  We rely on third-party courier services and do not operate our own delivery network.
                </p>
              </AccordionItem>

              <AccordionItem title="Domestic shipping (India)" icon="📦">
                <ul className="spList">
                  <li><b>Shipping charges (based on order value):</b><br/>Order below ₹199 — ₹49<br/>Order ₹199 to ₹498 — ₹59<br/>Order ₹499 and above — ₹79</li>
                  <li><b>Estimated delivery time:</b> 3–10 working days (location dependent)</li>
                </ul>
                <p>
                  Since all orders are dispatched from Hyderabad, deliveries to distant states may occasionally take slightly longer.
                </p>
                <p>
                  Once shipped, you will receive a tracking ID and tracking link via email/message.
                  Customers should track the shipment regularly and remain available for delivery communication.
                </p>
              </AccordionItem>

              <AccordionItem title="Important delivery guidelines" icon="📌">
                <p>Please ensure the following while placing your order:</p>
                <ul className="spList">
                  <li>Enter complete and accurate address</li>
                  <li>Double-check PIN code</li>
                  <li>Provide an active contact number</li>
                  <li>Stay available for courier calls</li>
                </ul>
                <p>If a parcel is returned due to:</p>
                <ul className="spList">
                  <li>Incorrect/incomplete address</li>
                  <li>Wrong PIN code</li>
                  <li>Unreachable phone number</li>
                  <li>Customer unavailability</li>
                  <li>Refusal to accept the parcel</li>
                </ul>
                <p><b>Re-dispatch</b> will be processed only after payment of shipping charges again. Refunds will not be provided.</p>
              </AccordionItem>

              <AccordionItem title="Mandatory unboxing video policy" icon="🎥">
                <p>Unboxing video is compulsory for complaints related to:</p>
                <ul className="spList">
                  <li>Damaged items</li>
                  <li>Missing products</li>
                  <li>Incorrect products</li>
                </ul>

                <div className="spChecklist">
                  <div className="spCheckItem">✅ Begin before opening the parcel</div>
                  <div className="spCheckItem">✅ Show sealed package from all sides (360°)</div>
                  <div className="spCheckItem">✅ Shipping label must be visible</div>
                  <div className="spCheckItem">✅ Continuous video (no cuts/edits/pauses)</div>
                </div>

                <p className="spMuted">
                  Complaints submitted without a proper unboxing video will be declined.
                </p>
              </AccordionItem>

              <AccordionItem title="International orders" icon="🌍">
                <p>
                  International shipping charges start from <b>₹600</b> and may vary based on parcel weight and destination.
                </p>
                <p>
                  International orders are processed via Instagram DM or WhatsApp due to variable rates.
                </p>
                <p>
                  Customs duties or additional fees (if applicable) must be borne by the customer.
                </p>
              </AccordionItem>

              <AccordionItem title="Urgent orders" icon="🚨">
                <p>
                  If you require urgent delivery, please contact us via WhatsApp before placing your order and mention your expected delivery date.
                </p>
                <p>
                  Alternative courier arrangements may involve revised shipping charges.
                </p>
              </AccordionItem>

              <AccordionItem title="Order confirmation policy" icon="📍">
                <ul className="spList">
                  <li>Changes to order details cannot be made</li>
                  <li>Orders cannot be cancelled after dispatch</li>
                  <li>Ayrine follows a strict no-refund policy once orders are processed</li>
                </ul>
                <p>Please review all details carefully before confirming your purchase.</p>
              </AccordionItem>
            </div>

            {/* Bottom emphasis card */}
            <div className="spNotice danger">
              <div className="spNoticeTitle">No-refund note</div>
              <div className="spNoticeText">
                Once orders are processed/confirmed, refunds are not offered. Shipping charges are non-refundable.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}